import { router } from '@inertiajs/react'
import { NotaryPortalLayout } from '@/components/layout/NotaryPortalLayout'
import { StatRow } from '@/components/backoffice/StatRow'
import { SectionHeader } from '@/components/backoffice/SectionHeader'
import { DataTable } from '@/components/backoffice/DataTable'
import { Badge } from '@/components/ui/badge'
import { PortalAppointment } from '@/types'

type DashboardStats = {
  pending_notarizations: number
  completed_this_month: number
  upcoming_appointments: number
}

type Props = {
  stats: DashboardStats
  todays_appointments: PortalAppointment[]
  upcoming_appointments: PortalAppointment[]
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatDateTime(iso: string) {
  return `${formatDate(iso)} at ${formatTime(iso)}`
}

function SessionModeBadge({ mode }: { mode: string }) {
  const label = mode.replace(/_/g, ' ')
  return (
    <Badge variant="outline" className="text-[11px] capitalize font-normal">
      {label}
    </Badge>
  )
}

function AppointmentCard({ appointment }: { appointment: PortalAppointment }) {
  return (
    <div
      className="border rounded-lg p-4 hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={() => router.visit(`/notary_portal/appointments/${appointment.id}`)}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-foreground">{appointment.business_name}</p>
        <SessionModeBadge mode={appointment.session_mode} />
      </div>
      <div className="space-y-1">
        <p className="text-[13px] text-muted-foreground">
          {formatTime(appointment.scheduled_at)} &middot; {appointment.duration_minutes} min
        </p>
        <p className="text-[12px] text-muted-foreground">
          {appointment.account_name} &middot; {appointment.documents_count} document{appointment.documents_count !== 1 ? 's' : ''}
        </p>
      </div>
      {appointment.cancelled && (
        <Badge className="mt-2 bg-red-100 text-red-700 border border-red-200/60 text-[11px] font-normal">
          Cancelled
        </Badge>
      )}
    </div>
  )
}

export default function DashboardShow({ stats, todays_appointments, upcoming_appointments }: Props) {
  return (
    <NotaryPortalLayout title="Dashboard">
      <div className="space-y-8">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Your notary activity at a glance</p>
        </div>

        <StatRow stats={[
          { label: 'pending', value: stats.pending_notarizations },
          { label: 'completed this month', value: stats.completed_this_month },
          { label: 'upcoming', value: stats.upcoming_appointments },
        ]} />

        <div>
          <SectionHeader title="Today's Appointments" count={todays_appointments.length} />
          {todays_appointments.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No appointments scheduled for today.</p>
            </div>
          ) : (
            <div className="grid gap-3 mt-3">
              {todays_appointments.map((apt) => (
                <AppointmentCard key={apt.id} appointment={apt} />
              ))}
            </div>
          )}
        </div>

        <div>
          <SectionHeader title="Upcoming (Next 7 Days)" count={upcoming_appointments.length} />
          <DataTable
            data={upcoming_appointments}
            keyFn={(a) => a.id}
            emptyMessage="No upcoming appointments in the next 7 days."
            onRowClick={(a) => router.visit(`/notary_portal/appointments/${a.id}`)}
            columns={[
              {
                key: 'business',
                header: 'Business',
                className: 'flex-1 min-w-0',
                render: (a) => (
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{a.business_name}</p>
                    <p className="text-[12px] text-muted-foreground truncate">{a.account_name}</p>
                  </div>
                ),
              },
              {
                key: 'datetime',
                header: 'Date & Time',
                className: 'w-44',
                render: (a) => (
                  <span className="text-sm text-muted-foreground">{formatDateTime(a.scheduled_at)}</span>
                ),
              },
              {
                key: 'mode',
                header: 'Mode',
                className: 'w-28',
                render: (a) => <SessionModeBadge mode={a.session_mode} />,
              },
            ]}
          />
        </div>
      </div>
    </NotaryPortalLayout>
  )
}
